import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Modal from "../../../components/Modal";
import SkeletonLoader from "../../../components/SkeletonLoader";
import { useUser } from "../../../hooks/useUser";
import { useOwnerRestaurantSelection } from "../../../hooks/useOwnerRestaurantSelection";
import { getTenantId } from "../../../utils/tenant";
import { useTenantTopicsAccess } from "../hooks/useTenantTopicsAccess";
import { addTenantTopic } from "../services/tenantTopicsService";
import { getTriviaMarketplacePacks } from "../services/triviaMarketplaceService";
import { redeemNamis } from "../../../services/transactionService";
import { useTransaction } from "../../../hooks/useTransaction";
import { buildQuizHeaders } from "../utils/buildQuizHeaders";
import {
  QuizCardSkeletonGroup,
  QuizEmptyMessage,
  QuizErrorMessage,
  QuizInlineInfo,
  QuizProfileWrapper,
  QuizSectionTitle,
  QuizTopicCard,
  QuizTopicHeader,
} from "../components/QuizProfileLayout";

const getPackId = (pack) => String(pack?._id || pack?.id || pack?.topic_id || "").trim();
const MARKETPLACE_PAGE_SIZE = 6;
const TRIVIA_COST_NAMIS = 5;

const getPageSlice = (items = [], currentPage = 1, pageSize = MARKETPLACE_PAGE_SIZE) => {
  const start = (currentPage - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

function MarketplacePagination({
  currentPage,
  totalItems,
  pageSize = MARKETPLACE_PAGE_SIZE,
  onPageChange,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <PaginationContainer>
      <PaginationInfo>
        Mostrando {startItem}-{endItem} de {totalItems}
      </PaginationInfo>
      <PaginationControls>
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ‹
        </PaginationButton>
        {getVisiblePages(currentPage, totalPages).map((page, index) =>
          page === "..." ? (
            <PaginationInfo key={`ellipsis-${index}`}>...</PaginationInfo>
          ) : (
            <PaginationButton
              key={page}
              type="button"
              $active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationButton>
          )
        )}
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          ›
        </PaginationButton>
      </PaginationControls>
    </PaginationContainer>
  );
}

const TriviaMarketplace = () => {
  const { userId } = useUser();
  const { selectedOwnerRestaurant } = useOwnerRestaurantSelection();
  const { getPoints } = useTransaction();

  // === Trivias state ===
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [packsError, setPacksError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [pendingPack, setPendingPack] = useState(null);
  const [localAcquiredPackIds, setLocalAcquiredPackIds] = useState([]);
  const [packsPage, setPacksPage] = useState(1);
  const [showNoBalanceModal, setShowNoBalanceModal] = useState(false);

  const headers = useMemo(() => {
    const storedName = typeof window !== "undefined" ? localStorage.getItem("hname") : "";
    return buildQuizHeaders({
      userId: userId || undefined,
      userName: selectedOwnerRestaurant?.name || selectedOwnerRestaurant?.restoName || storedName || undefined,
    });
  }, [selectedOwnerRestaurant?.name, selectedOwnerRestaurant?.restoName, userId]);

  const {
    topicIds: acquiredTopicIds,
    loading: loadingAccess,
    error: accessError,
    reload: reloadTopicAccess,
  } = useTenantTopicsAccess({ enabled: true });

  const acquiredPackSet = useMemo(
    () => new Set([...(acquiredTopicIds || []), ...localAcquiredPackIds].map((id) => String(id))),
    [acquiredTopicIds, localAcquiredPackIds]
  );

  const loadPacks = useCallback(async () => {
    setLoadingPacks(true);
    setPacksError("");
    try {
      const nextPacks = await getTriviaMarketplacePacks({ headers });
      setPacks(nextPacks);
    } catch (error) {
      setPacksError(error?.message || "No se pudieron cargar los packs de trivias.");
      setPacks([]);
    } finally {
      setLoadingPacks(false);
    }
  }, [headers]);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  const paginatedPacks = useMemo(
    () => getPageSlice(packs, packsPage, MARKETPLACE_PAGE_SIZE),
    [packs, packsPage]
  );

  useEffect(() => {
    setPacksPage(1);
  }, [packs.length]);

  // === Trivias handlers ===
  const handleOpenAcquireModal = (pack) => {
    setActionError("");
    setActionSuccess("");
    setPendingPack(pack || null);
  };

  const handleCloseAcquireModal = () => {
    if (actionLoadingId) return;
    setPendingPack(null);
  };

  const handleAcquirePack = async () => {
    const packId = getPackId(pendingPack);
    const tenantId = getTenantId();

    if (actionLoadingId) return;
    if (!packId) {
      setActionError("No se pudo identificar el pack seleccionado.");
      return;
    }
    if (!tenantId) {
      setActionError("No se pudo identificar el tenant para agregar el pack.");
      return;
    }

    setActionLoadingId(packId);
    setActionError("");
    setActionSuccess("");
    try {
      await redeemNamis({
        amount: TRIVIA_COST_NAMIS,
        reason: `Compra pack: ${pendingPack?.name || "Trivia"}`,
      });

      await getPoints();

      await addTenantTopic(tenantId, packId);
      setLocalAcquiredPackIds((prev) => (prev.includes(packId) ? prev : [...prev, packId]));
      await reloadTopicAccess();
      setActionSuccess(`Pack adquirido: ${pendingPack?.name || "Trivia"}.`);
      setPendingPack(null);
    } catch (error) {
      const msg = error?.message || "";
      if (msg.toLowerCase().includes("saldo insuficiente")) {
        setPendingPack(null);
        setShowNoBalanceModal(true);
      } else {
        setActionError(msg || "No se pudo completar la compra.");
      }
    } finally {
      setActionLoadingId("");
    }
  };

  const isTriviasLoading = loadingPacks || loadingAccess;
  const triviasVisibleError = packsError || accessError;

  return (
    <QuizProfileWrapper>
      <MarketplaceHeader>
        <h2>Marketplace</h2>
        <p>Explora packs de trivias para activar en tu almacén.</p>
      </MarketplaceHeader>

      {triviasVisibleError && <QuizErrorMessage>{triviasVisibleError}</QuizErrorMessage>}
      {actionError && <QuizErrorMessage>{actionError}</QuizErrorMessage>}
      {actionSuccess && <QuizInlineInfo>{actionSuccess}</QuizInlineInfo>}

      <QuizSectionTitle>Packs disponibles</QuizSectionTitle>

      {isTriviasLoading && (
        <QuizCardSkeletonGroup>
          <SkeletonLoader active width="100%" height="128px" borderRadius="12px" />
          <SkeletonLoader active width="100%" height="128px" borderRadius="12px" />
          <SkeletonLoader active width="100%" height="128px" borderRadius="12px" />
        </QuizCardSkeletonGroup>
      )}

      {!isTriviasLoading && !triviasVisibleError && packs.length === 0 && (
        <QuizEmptyMessage>No hay packs de trivias disponibles por ahora.</QuizEmptyMessage>
      )}

      {!isTriviasLoading && !triviasVisibleError && packs.length > 0 && (
        <>
          <PacksGrid>
            {paginatedPacks.map((pack, index) => {
              const packId = getPackId(pack);
              const isAcquired = acquiredPackSet.has(packId);
              const isActionLoading = actionLoadingId === packId;
              return (
                <QuizTopicCard key={packId || `pack-${index}`}>
                  <QuizTopicHeader>
                    <strong>{pack.name || "Pack de trivia"}</strong>
                    <p>{pack.description || "Preguntas preparadas para reforzar buenas prácticas del almacén."}</p>
                  </QuizTopicHeader>

                  <PackActionRow>
                    <AddButton
                      type="button"
                      onClick={() => handleOpenAcquireModal(pack)}
                      disabled={isAcquired || Boolean(actionLoadingId)}
                    >
                      {isAcquired ? "Agregado" : isActionLoading ? "Comprando..." : `Comprar - ${TRIVIA_COST_NAMIS} Namis`}
                    </AddButton>
                  </PackActionRow>
                </QuizTopicCard>
              );
            })}
          </PacksGrid>
          <MarketplacePagination
            currentPage={packsPage}
            totalItems={packs.length}
            pageSize={MARKETPLACE_PAGE_SIZE}
            onPageChange={setPacksPage}
          />
        </>
      )}

      <Modal
        isOpen={Boolean(pendingPack)}
        onClose={handleCloseAcquireModal}
        label="Confirmar compra"
        acceptButtonText={actionLoadingId ? "Comprando..." : `Pagar ${TRIVIA_COST_NAMIS} Namis`}
        cancelButtonText="Cancelar"
        onAccept={handleAcquirePack}
        onCancel={handleCloseAcquireModal}
        acceptButtonDisabled={Boolean(actionLoadingId)}
        cancelButtonDisabled={Boolean(actionLoadingId)}
        width="500px"
      >
        <ModalText>
          ¿Deseas comprar el pack <strong>{pendingPack?.name || "este pack"}</strong> por{" "}
          <strong>{TRIVIA_COST_NAMIS} Namis</strong>?
          <br />
          <small>Se descontarán de tu saldo disponible.</small>
        </ModalText>
      </Modal>

      <Modal
        isOpen={showNoBalanceModal}
        onClose={() => setShowNoBalanceModal(false)}
        label="Saldo insuficiente"
        acceptButtonText="Entendido"
        onAccept={() => setShowNoBalanceModal(false)}
        width="420px"
      >
        <ModalText>
          No tienes suficientes <strong>Ñamis</strong> para comprar este pack.
          <br />
          <small>Cada pack cuesta <strong>{TRIVIA_COST_NAMIS} Namis</strong>.</small>
        </ModalText>
      </Modal>
    </QuizProfileWrapper>
  );
};

export default TriviaMarketplace;

const MarketplaceHeader = styled.header`
  display: grid;
  gap: 6px;
  margin-bottom: 18px;

  h2 {
    margin: 0;
    color: #111827;
    font-size: 24px;
  }

  p {
    margin: 0;
    color: #6b7280;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const PacksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const PackActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AddButton = styled.button`
  background: #ff0102;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.15s ease, background 0.2s ease;

  &:hover:not(:disabled) {
    background: #d90002;
    transform: scale(1.02);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 22px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 0 4px;
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: #777;
  text-align: center;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: ${({ disabled, $active }) => ($active ? "#111" : disabled ? "#f3f3f3" : "#fff")};
  color: ${({ disabled, $active }) => ($active ? "#fff" : disabled ? "#aaa" : "#333")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? "700" : "500")};
`;

const ModalText = styled.p`
  margin: 0 0 18px;
  color: #334155;
  line-height: 1.5;
  text-align: center;
`;
